import { CloseOutlined, MailOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Container } from '@/components/Container';
import { InputWithCopyButton } from '@/components/revisar/input/InputWithCopyButton';
import { useEmpresaQuery } from '@/features/empresa/hooks/useEmpresaQuery';
import { isEmail } from '@/std/validate';

const { Text } = Typography;
const { TextArea } = Input;

export type ICompartilhar = {
    link: string;
    assunto: string;
    mensagem: string;
    idpk: number;
    email?: string | null;
    telefone?: string | null;
};

export type FormCompartilharValues = {
    email?: string | null;
    telefone?: string | null;
    mensagem?: string | null;
    assunto?: string | null;
    com_copia?: string | null;
    link?: string | null;
    cnpjcpf?: string | number | null;
    razao_social?: string | null;
};

export function FormCompartilhar({
    link,
    mensagem,
    assunto,
    telefone,
    email,
    onClose,
    enviarEmail,
    enviarWhatsapp,
}: {
    link: string;
    mensagem: string;
    assunto: string;
    telefone: string | undefined | null;
    email: string | undefined | null;
    onClose: () => void;
    enviarEmail?: (values: FormCompartilharValues) => Promise<void>;
    enviarWhatsapp?: (values: FormCompartilharValues) => Promise<void>;
}) {
    const [exibirOpcoesAvancadas, setExibirOpcoesAvancadas] = useState(false);
    const [disableBtnEmail, setDisableBtnEmail] = useState(true);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);

    const { empresa } = useEmpresaQuery();
    const nomeEmpresa = empresa?.emp_nome_fantasia || '';

    const [formCompartilhamento] = Form.useForm<FormCompartilharValues>();
    const telefoneWatch = Form.useWatch('telefone', formCompartilhamento);

    useEffect(() => {
        formCompartilhamento.setFieldsValue({
            assunto,
            com_copia: '',
            email,
            link,
            mensagem: `${mensagem}\n\n[Esta é uma mensagem enviada automaticamente pelo aplicativo utilizado por ${nomeEmpresa}]`,
            telefone,
        });
    }, [email, telefone, link, formCompartilhamento, mensagem, assunto, nomeEmpresa]);

    useEffect(() => {
        if (email) {
            setDisableBtnEmail(false);
        }
    }, [email]);

    const handleWhatsapp = async (): Promise<void> => {
        if (!enviarWhatsapp) {
            return;
        }

        setLoadingWhatsApp(true);

        try {
            const values = formCompartilhamento.getFieldsValue();
            await enviarWhatsapp(values);
        } catch {
            //
        }

        setLoadingWhatsApp(false);
    };

    const handleEmail = async (): Promise<void> => {
        if (!enviarEmail) {
            return;
        }

        setLoadingEmail(true);

        try {
            const values = formCompartilhamento.getFieldsValue();
            await enviarEmail(values);
        } catch {
            //
        }

        setLoadingEmail(false);
    };

    return (
        <div style={{ marginLeft: 5 }}>
            <Form
                form={formCompartilhamento}
                id='formCompartilhamento'
                labelAlign='left'
                layout='vertical'
                name='formCompartilhamento'
            >
                <Row>
                    <Text>Mensagem:</Text>
                </Row>

                <Row>
                    <Form.Item name='mensagem'>
                        <TextArea rows={5} style={{ width: 350 }} />
                    </Form.Item>
                </Row>

                <Row>
                    <Text>Link para compartilhamento:</Text>
                </Row>

                <Form.Item name='link'>
                    <InputWithCopyButton showOpenNewTabBtn={true} text_to_copy={link} />
                </Form.Item>

                {enviarEmail && (
                    <Container title='Email'>
                        <Row gutter={6}>
                            <Col>
                                <Form.Item
                                    label='Email do destinatário:'
                                    name='email'
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input
                                        onChange={(evt) => {
                                            if (isEmail(evt.target.value)) {
                                                setDisableBtnEmail(false);
                                            } else {
                                                setDisableBtnEmail(true);
                                            }
                                        }}
                                        placeholder='digite um email'
                                        style={{ width: 330 }}
                                        type='email'
                                    />
                                </Form.Item>

                                <Button
                                    onClick={() => setExibirOpcoesAvancadas(!exibirOpcoesAvancadas)}
                                    style={{ paddingLeft: 0 }}
                                    type='link'
                                >
                                    {`${exibirOpcoesAvancadas ? 'Esconder' : 'Exibir'} opções avançadas`}
                                </Button>
                            </Col>
                        </Row>

                        {exibirOpcoesAvancadas && (
                            <>
                                <Row>
                                    <Text>Assunto</Text>
                                </Row>

                                <Row>
                                    <Form.Item name='assunto'>
                                        <Input style={{ width: 330 }} />
                                    </Form.Item>
                                </Row>

                                <Row>
                                    <Text>Com Cópia</Text>
                                </Row>

                                <Row>
                                    <Form.Item name='com_copia'>
                                        <Input style={{ width: 330 }} type='email' />
                                    </Form.Item>
                                </Row>
                            </>
                        )}

                        <Row>
                            <Button
                                disabled={disableBtnEmail}
                                icon={<MailOutlined style={{ padding: 5 }} />}
                                loading={loadingEmail}
                                onClick={handleEmail}
                                style={{
                                    display: 'block',
                                    margin: '0 auto',
                                    width: 105,
                                }}
                                type='primary'
                            >
                                Enviar
                            </Button>
                        </Row>
                    </Container>
                )}

                {enviarWhatsapp && (
                    <Container className='mt-2' title='WhatsApp'>
                        <Row gutter={6}>
                            <Row>
                                <Text>Celular com DDD e somente números:</Text>
                            </Row>

                            <Col>
                                <Form.Item name='telefone'>
                                    <InputNumber
                                        controls={false}
                                        placeholder='deixe vazio para escolher de sua lista de contatos'
                                        style={{ width: 330 }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Button
                                disabled={!telefoneWatch}
                                icon={<WhatsAppOutlined style={{ padding: 5 }} />}
                                loading={loadingWhatsApp}
                                onClick={handleWhatsapp}
                                style={{
                                    background: '#41b883',
                                    borderColor: '#41b883',
                                    display: 'block',
                                    margin: '0 auto',
                                    width: 105,
                                }}
                            >
                                Enviar
                            </Button>
                        </Row>
                    </Container>
                )}

                <Row justify='center' style={{ marginTop: 25 }}>
                    <Button icon={<CloseOutlined />} onClick={onClose}>
                        Fechar
                    </Button>
                </Row>
            </Form>
        </div>
    );
}
